import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
  Alert,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconArrowsShuffle,
  IconScale,
  IconShieldCheck,
  IconShieldOff,
  IconTemplate,
  IconTournament,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { DetailPageSkeleton } from "../../../../components/SkeletonLoader";
import { PresetSelector } from "./components/PresetSelector";
import { STEP_RULE_SET_QUERY } from "./graphql/queries";
import { PRESET_LABELS, StepRuleSetQueryResult } from "./types";
import { ValidateButton } from "./validation/ValidateButton";
import { RuleSetValidationPanel } from "./validation/RuleSetValidationPanel";
import { ValidationJobHistoryTab } from "./validation/ValidationJobHistoryTab";

const truncate = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max)}…` : value;

export const RuleSetOverview = () => {
  const { stepId, tournamentId } = useParams();
  const navigate = useNavigate();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const { data, loading, error } = useQuery<StepRuleSetQueryResult>(
    STEP_RULE_SET_QUERY,
    {
      variables: { tournamentStepId: stepId ?? "" },
      skip: !stepId,
    },
  );

  if (!stepId || !tournamentId) {
    return <Text c="red">Missing route parameters</Text>;
  }

  if (loading && !data) return <DetailPageSkeleton />;

  if (error) {
    return (
      <Stack>
        <Title>Rules</Title>
        <Text c="red">Failed to load rule set: {error.message}</Text>
      </Stack>
    );
  }

  const ruleSet = data?.stepRuleSet;
  const basePath = `/tournaments/${tournamentId}/steps/${stepId}/rules`;

  if (!ruleSet) {
    return (
      <div className="wp-fade-in">
        <Stack>
          <Title>Rules</Title>
          <Alert
            color="yellow"
            icon={<IconAlertTriangle size={16} />}
            title="No rule set configured"
          >
            This step has no rule set. Generation is blocked until you apply a
            preset or build a custom rule set.
          </Alert>
          <PresetSelector stepId={stepId} />
        </Stack>
      </div>
    );
  }

  const scoring = ruleSet.scoringRuleSet;
  const advancementRules = ruleSet.advancementRules ?? [];
  const crossStepRules = ruleSet.crossStepRules ?? [];
  const groupTemplates = ruleSet.structureTemplate?.groupTemplates ?? [];
  const hasStructure = groupTemplates.length > 0;

  const sortedTiebreakers = scoring
    ? [...scoring.tiebreakers].sort((a, b) => a.order - b.order)
    : [];
  const previewAdvancementRules = [...advancementRules]
    .sort((a, b) => a.order - b.order)
    .slice(0, 5);
  const sortedGroupTemplates = [...groupTemplates].sort(
    (a, b) => a.order - b.order,
  );
  const previewCrossStepRules = crossStepRules.slice(0, 5);

  return (
    <div className="wp-fade-in">
      <Stack>
        {!ruleSet.validated && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="yellow"
            mb="md"
            title="Not validated"
          >
            This rule set has not been validated yet. Step generation is blocked
            until validation passes. Click <strong>Validate</strong> above to
            run a full simulation.
          </Alert>
        )}
        <Group justify="space-between">
          <Group gap="sm">
            <Title>Rules</Title>
            {ruleSet.presetName && (
              <Badge size="lg" variant="light" color="teal">
                Based on: {PRESET_LABELS[ruleSet.presetName]}
              </Badge>
            )}
            {ruleSet.teamCount != null && (
              <Badge size="lg" variant="light" color="blue">
                {ruleSet.teamCount} Teams
              </Badge>
            )}
            {ruleSet.validated ? (
              <Tooltip
                label={`Validated at ${
                  ruleSet.validatedAt
                    ? new Date(ruleSet.validatedAt).toLocaleString()
                    : "unknown"
                }`}
              >
                <Badge
                  size="lg"
                  variant="light"
                  color="green"
                  leftSection={<IconShieldCheck size={14} />}
                >
                  Validated
                </Badge>
              </Tooltip>
            ) : (
              <Tooltip label="Run a successful simulation to validate rules before generating">
                <Badge
                  size="lg"
                  variant="light"
                  color="yellow"
                  leftSection={<IconShieldOff size={14} />}
                >
                  Not Validated
                </Badge>
              </Tooltip>
            )}
          </Group>
          <Group gap="sm">
            <ValidateButton
              stepRuleSetId={ruleSet.id}
              validated={ruleSet.validated}
              onJobStart={setActiveJobId}
            />
            <Badge size="lg" variant="light">
              v{ruleSet.version}
            </Badge>
          </Group>
        </Group>

        <Tabs defaultValue="rules" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="rules">Rules</Tabs.Tab>
            <Tabs.Tab value="validation-history">Validation history</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="rules" pt="md">
            <Stack>
              <SimpleGrid cols={2}>
                <Card
                  withBorder
                  className="wp-card-interactive"
                  onClick={() => navigate(`${basePath}/scoring`)}
                >
                  <Stack gap="xs">
                    <Group>
                      <IconScale size={20} />
                      <Text fw={600}>Scoring</Text>
                      <IconArrowRight
                        size={14}
                        style={{ marginLeft: "auto" }}
                      />
                    </Group>
                    {scoring ? (
                      <>
                        <Text size="sm" c="dimmed">
                          Aggregation: {scoring.aggregation}
                        </Text>
                        {scoring.aggregation === "POINTS" && (
                          <Text size="xs" c="dimmed">
                            W:{scoring.winPoints} L:{scoring.lossPoints} D:
                            {scoring.drawPoints} FF:{scoring.forfeitPoints}
                          </Text>
                        )}
                        {scoring.aggregation === "FORMULA" &&
                          scoring.formula && (
                            <Code block>{truncate(scoring.formula, 160)}</Code>
                          )}
                        <Text size="xs" c="dimmed">
                          {sortedTiebreakers.length} tiebreaker(s)
                        </Text>
                        {sortedTiebreakers.length > 0 && (
                          <Stack gap={2}>
                            {sortedTiebreakers.map((tb, idx) => (
                              <Group key={`${tb.order}-${tb.type}`} gap={6}>
                                <Badge size="xs" variant="outline">
                                  {idx + 1}
                                </Badge>
                                <Text size="xs" c="dimmed">
                                  {tb.type}
                                  {tb.sortOrder ? ` (${tb.sortOrder})` : ""}
                                </Text>
                              </Group>
                            ))}
                          </Stack>
                        )}
                      </>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Not configured
                      </Text>
                    )}
                  </Stack>
                </Card>

                <Card
                  withBorder
                  className="wp-card-interactive"
                  onClick={() => navigate(`${basePath}/advancement`)}
                >
                  <Stack gap="xs">
                    <Group>
                      <IconTournament size={20} />
                      <Text fw={600}>Advancement Rules</Text>
                      <IconArrowRight
                        size={14}
                        style={{ marginLeft: "auto" }}
                      />
                    </Group>
                    <Text size="sm" c="dimmed">
                      {advancementRules.length} rule(s) configured
                    </Text>
                    {previewAdvancementRules.length > 0 && (
                      <Stack gap={6}>
                        {previewAdvancementRules.map((rule) => (
                          <Stack key={rule.id} gap={2}>
                            <Group gap={6}>
                              <Text size="xs" fw={600}>
                                {rule.name}
                              </Text>
                              <Badge size="xs" variant="outline">
                                {rule.triggerType}
                              </Badge>
                              {!rule.enabled && (
                                <Badge size="xs" color="gray" variant="light">
                                  disabled
                                </Badge>
                              )}
                            </Group>
                            {rule.condition && (
                              <Code block>{truncate(rule.condition, 140)}</Code>
                            )}
                          </Stack>
                        ))}
                        {advancementRules.length >
                          previewAdvancementRules.length && (
                          <Text size="xs" c="dimmed">
                            +{" "}
                            {advancementRules.length -
                              previewAdvancementRules.length}{" "}
                            more
                          </Text>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Card>

                <Card
                  withBorder
                  className="wp-card-interactive"
                  onClick={() => navigate(`${basePath}/structure`)}
                >
                  <Stack gap="xs">
                    <Group>
                      <IconTemplate size={20} />
                      <Text fw={600}>Structure Template</Text>
                      <IconArrowRight
                        size={14}
                        style={{ marginLeft: "auto" }}
                      />
                    </Group>
                    <Text size="sm" c="dimmed">
                      {hasStructure
                        ? `${groupTemplates.length} group template(s)`
                        : "Not configured"}
                    </Text>
                    {hasStructure && (
                      <Stack gap={2}>
                        {sortedGroupTemplates.map((g, i) => (
                          <Group key={g.id} gap={6}>
                            <Badge size="xs" variant="outline">
                              {i + 1}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              {g.name ?? `Group ${i + 1}`}
                              {g.roundTemplates?.length
                                ? ` — ${g.roundTemplates.length} round(s)`
                                : ""}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card>

                <Card
                  withBorder
                  className="wp-card-interactive"
                  onClick={() => navigate(`${basePath}/cross-step`)}
                >
                  <Stack gap="xs">
                    <Group>
                      <IconArrowsShuffle size={20} />
                      <Text fw={600}>Cross-Step Rules</Text>
                      <IconArrowRight
                        size={14}
                        style={{ marginLeft: "auto" }}
                      />
                    </Group>
                    <Text size="sm" c="dimmed">
                      {crossStepRules.length} rule(s) configured
                    </Text>
                    {previewCrossStepRules.length > 0 && (
                      <Stack gap={2}>
                        {previewCrossStepRules.map((rule, idx) => (
                          <Group
                            key={`${rule.sourceStepId ?? "rel"}-${rule.sourceRelative ?? "abs"}-${rule.usage.type}-${idx}`}
                            gap={6}
                          >
                            <Badge size="xs" variant="outline">
                              {rule.usage.type}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              {rule.dataSource}
                            </Text>
                          </Group>
                        ))}
                        {crossStepRules.length >
                          previewCrossStepRules.length && (
                          <Text size="xs" c="dimmed">
                            +{" "}
                            {crossStepRules.length -
                              previewCrossStepRules.length}{" "}
                            more
                          </Text>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Card>
              </SimpleGrid>

              <Divider />

              <PresetSelector stepId={stepId} />

              <RuleSetValidationPanel jobId={activeJobId} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="validation-history" pt="md">
            <ValidationJobHistoryTab
              stepRuleSetId={ruleSet.id}
              onOpenJob={(jobId) => {
                setActiveJobId(jobId);
              }}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </div>
  );
};
